// FilterDropdown.jsx
//
// Універсальний дропдаун-фільтр з чекбокс-стилем пунктів (той самий вигляд,
// що й у списку заявок ApplicationsPage). Підтримує два режими:
//
//   mode="single" — поводиться як звичайний <select>: активна одна опція,
//                    клік по пункту одразу обирає значення і закриває меню.
//                    value/onChange працюють зі скалярним значенням.
//
//   mode="multi"  — незалежні тумблери (як фільтри заявок): можна активувати
//                    кілька пунктів одночасно, меню лишається відкритим при
//                    кліках і закривається тільки по кліку поза ним.
//                    value — масив активних значень, onChange повертає
//                    значення пункту, по якому клікнули (перемикання —
//                    відповідальність батьківського компонента).
//
// options: [{ value, label, disabled? }] або { divider: true } для розділювача
//
// indicator — візуальний маркер обраного пункту:
//   'checkbox' — квадратик з ✓ (природньо для multi: кілька незалежних тумблерів)
//   'radio'    — кружечок з крапкою (природньо для single: "один з багатьох")
//   'none'     — без маркера, лише підсвітка активного пункту фоном/кольором
//                (для однозначних списків типу "рік", де й без кружечка зрозуміло)
// За замовчуванням обирається автоматично: multi → 'checkbox', single → 'radio'.
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import './FilterDropdown.css';

const FilterDropdown = ({
  label,
  options,
  value,
  onChange,
  mode = 'single',
  indicator,
  placeholder = 'Оберіть',
  onClear,
  clearLabel = 'Скинути',
  renderSelectedLabel,
  disabled = false,
  className = '',
}) => {
  const indicatorType = indicator || (mode === 'multi' ? 'checkbox' : 'radio');

  const selectedValues = mode === 'multi' ? (Array.isArray(value) ? value : []) : null;

  const isActive = (opt) =>
    mode === 'multi' ? selectedValues.includes(opt.value) : value === opt.value;

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    onChange(opt.value);
  };

  const selectableOptions = options.filter((o) => !o.divider);

  // Для мультивибору: один пункт — показуємо його назву; два і більше — кількість
  // ("Обрано: 3"), а не конкатенацію назв, бо та все одно обрізається ellipsis і
  // видно тільки початок першої назви — незрозуміло, скільки всього обрано.
  const selectedLabels = () => {
    const selected = selectableOptions.filter((o) => selectedValues.includes(o.value));
    return selected.map((o) => o.label);
  };

  const buttonText = () => {
    if (mode === 'multi') {
      const selected = selectableOptions.filter((o) => selectedValues.includes(o.value));
      if (renderSelectedLabel) return renderSelectedLabel(selected);
      if (!selected.length) return placeholder;
      if (selected.length === 1) return selected[0].label;
      return `Обрано: ${selected.length}`;
    }
    const found = selectableOptions.find((o) => o.value === value);
    return found ? found.label : placeholder;
  };

  // Повний список обраного — у title, щоб можна було глянути по ховеру, не
  // відкриваючи меню (актуально саме коли кнопка показує лише кількість).
  const buttonTitle = () => {
    if (mode !== 'multi') return undefined;
    const labels = selectedLabels();
    return labels.length > 1 ? labels.join(', ') : undefined;
  };

  return (
    <div className={`ps-filter-field ${className}`}>
      {label && <label className="publicstatistic-filter-label">{label}</label>}
      <Dropdown autoClose={mode === 'multi' ? 'outside' : true}>
        <Dropdown.Toggle variant="outline-secondary" className="filter-btn" disabled={disabled} title={buttonTitle()}>
          <span className="filter-btn-text">{buttonText()}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="applications-filter-menu">
          {options.map((opt, i) =>
            opt.divider ? (
              <Dropdown.Divider key={`divider-${i}`} />
            ) : (
              <Dropdown.Item
                key={opt.value}
                as="button"
                disabled={opt.disabled}
                className={`filter-item ${isActive(opt) ? 'active' : ''}`}
                onClick={() => handleSelect(opt)}
              >
                {indicatorType !== 'none' && (
                  <div className={`filter-indicator filter-indicator--${indicatorType}`}>
                    {isActive(opt) && (
                      indicatorType === 'radio'
                        ? <span className="filter-indicator-dot" />
                        : <span className="filter-checkmark">✓</span>
                    )}
                  </div>
                )}
                {opt.label}
              </Dropdown.Item>
            )
          )}

          {onClear && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item as="button" className="filter-clear" onClick={onClear}>
                {clearLabel}
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default FilterDropdown;